var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useState } from "react";
import { useLogging } from "../../../../../Shared/hooks/useLogging/useLogging";
import { BillingInformationContext } from "../../../BillingInformationContext";
import { usePaymentMethods } from "../../../../../SWR/Payments/usePaymentMethods";
import { useSetPaymentMethodAsPrimaryMutation, useDeactivatePaymentMethodMutation, useUpdatePaymentMethodMutation, } from "../../../../../SWR/Payments/usePaymentMethodMutations";
import { initPaymentMethodUpdate } from "./PaymentMethodUpdate-utils";
/**
 * Backward-compatible hook for updating payment methods.
 *
 * ✅ Uses SWR mutations internally - cache is properly updated
 * ✅ Safe to use - no risk of falling out of sync
 *
 * This hook wraps three SWR mutations:
 * - `updatePaymentMethod` → `useUpdatePaymentMethodMutation()`
 * - `updatePrimaryPaymentMethod` → `useSetPaymentMethodAsPrimaryMutation()`
 * - `deactivatePaymentMethod` → `useDeactivatePaymentMethodMutation()`
 *
 * Provides:
 * - Loading and error state management
 * - Backward-compatible API for existing code
 * - `initPaymentMethodUpdate` for processor-specific flows
 *
 * For new code, you can use the mutation hooks directly if you prefer,
 * but this hook is fully supported and SWR-powered.
 */
export const usePaymentMethodUpdate = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    // Get payment data from SWR
    const { customerCreditCards } = usePaymentMethods();
    // Get UI state setters from context
    const { setClientSecret, setToken, setPublicKey, setPaymentProfileId, setPaymentProcessor } = useContext(BillingInformationContext);
    const { datadogLog } = useLogging();
    // Get SWR mutations
    const { trigger: updateMutation } = useUpdatePaymentMethodMutation();
    const { trigger: setPrimaryMutation } = useSetPaymentMethodAsPrimaryMutation();
    const { trigger: deactivateMutation } = useDeactivatePaymentMethodMutation();
    return {
        initPaymentMethodUpdate: initPaymentMethodUpdate(setLoading, setToken, setPublicKey, setClientSecret, setPaymentProfileId, setPaymentProcessor, datadogLog),
        // Wrapper around SWR mutation
        updatePaymentMethod: (body) => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            setError(undefined);
            try {
                // Extract paymentMethodId as string
                const paymentMethodId = typeof body.paymentMethodId === "string" ? body.paymentMethodId : undefined;
                if (!paymentMethodId) {
                    throw new Error("Payment method ID is required");
                }
                yield updateMutation({
                    paymentMethodId,
                    request: body,
                });
                return { success: true };
            }
            catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Failed to update payment profile.";
                setError(errorMessage);
                return { success: false, message: errorMessage };
            }
            finally {
                setLoading(false);
            }
        }),
        // Wrapper around SWR mutation
        updatePrimaryPaymentMethod: (paymentMethodId) => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            setError(undefined);
            try {
                yield setPrimaryMutation(paymentMethodId);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "Failed to update primary payment method");
            }
            finally {
                setLoading(false);
            }
        }),
        // Wrapper around SWR mutation
        deactivatePaymentMethod: (paymentMethodId) => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            setError(undefined);
            try {
                yield deactivateMutation(paymentMethodId);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "Failed to deactivate payment method");
            }
            finally {
                setLoading(false);
            }
        }),
        loading,
        error,
    };
};
